// @ts-strict-ignore
/** Popover related utilities. */
import {capitalize, commaizeNumber} from 'common/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {BarType, SelectedBar} from 'common/report/insights/charts/funnels-step-chart/types';
import {CountType, FunnelSegment} from 'common/report/funnels/models/types';
import {formatPercent} from 'common/report/funnels/util';
import {LocalizationStore} from 'common/i18n/store';
import {ConversionColumnVizFormatter} from 'common/charts/visualization/conversion-column-viz/types';

import {getGroupLabel} from './util';
import {ButtonOption} from './cohorts';

export interface SlottedContent {
  text: string;
  subTextLines: string[];
}

interface StepConversionMetrics {
  count: string;
  stepConvPercent: string;
  overallConvPercent: string;
  dropOffCount: string;
  stepDropOffPercent: string;
  overallDropOffPercent: string;
}

enum ComparisonPeriod {
  None = `NONE`,
  Previous = `PREVIOUS`,
  Current = `CURRENT`,
}

interface SlottedContentGeneratorArgs {
  metrics: StepConversionMetrics;
  unit: string;
  stepIdx: number;
  comparisonPeriod?: ComparisonPeriod;
}

interface PopoverOptions {
  canViewInFlows: boolean;
  canViewReplays: boolean;
  colors: string[];
  dataGroup?: DataGroup;
  dataGroupName: string;
  disabledCreateCohortButtonMessage: string;
  disabledViewInFlowsButtonMessage: string;
  disabledViewReplaysButtonMessage: string;
  disableViewUsersButtonMessage: string;
}

export class ChartPopover {
  public declare bar: SelectedBar;
  private declare segments: FunnelSegment[];
  private declare pastSegments: FunnelSegment[];
  private declare currentSegments: FunnelSegment[];
  private declare i18n: LocalizationStore;
  private declare formatter: ConversionColumnVizFormatter;
  private declare options: Partial<PopoverOptions>;
  private declare hasComparison: boolean;

  constructor(
    segments: FunnelSegment[],
    pastSegments: FunnelSegment[],
    currentSegments: FunnelSegment[],
    bar: SelectedBar,
    i18n: LocalizationStore,
    formatter: ConversionColumnVizFormatter,
    options: Partial<PopoverOptions> = {},
    hasComparison = false,
  ) {
    this.segments = segments;
    this.pastSegments = pastSegments;
    this.currentSegments = currentSegments;
    this.bar = bar;
    this.i18n = i18n;
    // Never abbreviate popover values.
    this.formatter = (value, isPercent) => formatter(value, isPercent, {}, {abbrev: false});
    this.options = options;
    this.hasComparison = hasComparison;

    if (options && options.colors && options.colors.length < segments.length) {
      throw new Error(`not enough colors provided`);
    }
  }

  public getButtonOptions(): ButtonOption[] {
    let buttonOptions = [];
    const {index, barIdx, type} = this.bar;
    const isFirstStep = index === 0;

    const dataGroupName = capitalize(this.options.dataGroupName);
    const viewGroupLabel =
      dataGroupName === `Users`
        ? this.i18n.t(`chart-actions.view-users`)
        : this.i18n.t(`chart-actions.view-group`, {group: dataGroupName});
    buttonOptions.push({
      label: viewGroupLabel,
      icon: `team`,
      value: `view-users`,
      tooltip:
        type === BarType.CONVERTED
          ? `${capitalize(viewGroupLabel, {strict: true})} ${this.i18n.t(`chart-actions.funnels.who-completed-step`)}`
          : `${capitalize(viewGroupLabel, {strict: true})} ${this.i18n.t(`chart-actions.funnels.who-dropped-off`)}`,
      ...(this.options.disableViewUsersButtonMessage
        ? {
            isDisabled: true,
            tooltip: this.options.disableViewUsersButtonMessage,
          }
        : {}),
    });

    if (this.options.canViewInFlows && !isFirstStep) {
      const viewInFlowsButtonOptions = this.getViewInFlowsButtonOptions(index);
      let disabledViewInFlowsButtonMessage = this.options.disabledViewInFlowsButtonMessage;

      if (this.segments[barIdx].prop === `$other`) {
        disabledViewInFlowsButtonMessage = `View as Flow is disabled for $other segment`;
      }

      if (disabledViewInFlowsButtonMessage) {
        viewInFlowsButtonOptions.forEach((option) => {
          // @ts-ignore TODO: SectionItem and ButtonOption are in a weird state of overlap has to be untangled
          option.isDisabled = true;
          // @ts-ignore
          option.tooltip = disabledViewInFlowsButtonMessage;
        });
      }

      buttonOptions = [...buttonOptions, ...viewInFlowsButtonOptions];
    }

    if (this.options.canViewReplays && !this.options.dataGroup) {
      buttonOptions.push({
        label: this.i18n.t(`chart-actions.view-replays`),
        icon: `replay`,
        value: `view-replays`,
        tooltip:
          type === BarType.CONVERTED
            ? this.i18n.t(`chart-actions.funnels.replays-step-tooltip`)
            : this.i18n.t(`chart-actions.funnels.replays-previous-tooltip`),
        ...(this.options.disabledViewReplaysButtonMessage
          ? {isDisabled: true, tooltip: this.options.disabledViewReplaysButtonMessage}
          : {}),
      });
    }
    return buttonOptions;
  }

  public isSegmented() {
    return this.hasComparison ? this.segments.length > 2 : this.segments.length > 1;
  }

  public isDropOff() {
    return this.bar.type === BarType.DROP_OFF;
  }

  public getSlottedContent(countType: CountType): SlottedContent[] {
    const {type: barType, index: stepIdx, barIdx: segmentIdx} = this.bar;
    const groupLabel = getGroupLabel(this.options.dataGroup, {lowerCase: true});
    const unit = countType === `unique` ? `unique ${groupLabel}` : this.i18n.t(`chart-actions.tooltip.totals`);
    let slottedContent = [];

    if (this.hasComparison) {
      const currentSegmentName = this.segments[segmentIdx].propName;
      const currentSegmentIdx = this.currentSegments.findIndex((segment) => segment.propName === currentSegmentName);
      const pastSegmentIdx = this.pastSegments.findIndex((segment) => segment.propName === currentSegmentName);
      const currentMetrics = this.getConversionMetrics(this.currentSegments, currentSegmentIdx, stepIdx);
      const pastMetrics = this.getConversionMetrics(this.pastSegments, pastSegmentIdx, stepIdx);
      switch (barType) {
        case BarType.CONVERTED:
          slottedContent = [
            this.getConvertedSlotContent({
              metrics: currentMetrics,
              comparisonPeriod: ComparisonPeriod.Current,
              unit,
              stepIdx,
            }),
            this.getConvertedSlotContent({
              metrics: pastMetrics,
              comparisonPeriod: ComparisonPeriod.Previous,
              unit,
              stepIdx,
            }),
          ];
          break;
        case BarType.DROP_OFF: {
          if (stepIdx === 0) {
            break;
          }
          slottedContent = [
            ...this.getDropOffSlottedContent({
              metrics: currentMetrics,
              comparisonPeriod: ComparisonPeriod.Current,
              unit,
              stepIdx,
            }),
            ...this.getDropOffSlottedContent({
              metrics: pastMetrics,
              comparisonPeriod: ComparisonPeriod.Previous,
              unit,
              stepIdx,
            }),
          ];
          break;
        }
      }
    } else {
      const metrics = this.getConversionMetrics(this.segments, segmentIdx, stepIdx);
      switch (barType) {
        case BarType.CONVERTED:
          slottedContent = [this.getConvertedSlotContent({metrics, unit, stepIdx})];
          break;
        case BarType.DROP_OFF: {
          if (stepIdx === 0) {
            break;
          }
          slottedContent = this.getDropOffSlottedContent({metrics, unit, stepIdx});
          break;
        }
      }
    }
    return slottedContent;
  }

  private getViewInFlowsButtonOptions(index: number): ButtonOption[] {
    const stepLabel = this.i18n.t(`chart-actions.funnels.step-from-to`, {from: index, to: index + 1});
    const viewAsFlowLabel = this.hasComparison
      ? this.i18n.t(`chart-actions.funnels.view-as-flow-comparison`)
      : this.i18n.t(`chart-actions.funnels.view-as-flow`);
    const mainButtonLabel = `${viewAsFlowLabel} ${stepLabel}`;

    return [
      {
        label: mainButtonLabel,
        value: `view-in-flows`,
      },
    ];
  }

  private getConvertedSlotContent({
    metrics,
    unit,
    stepIdx,
    comparisonPeriod = ComparisonPeriod.None,
  }: SlottedContentGeneratorArgs): SlottedContent {
    let content;
    if (comparisonPeriod === ComparisonPeriod.None) {
      content = {
        text: `${metrics.overallConvPercent} ${this.i18n.t(`chart-actions.funnels.completed-this-step`)}`,
        subTextLines: [
          ...(stepIdx > 1
            ? [`${metrics.stepConvPercent} ${this.i18n.t(`chart-actions.funnels.completed-from-previous`)}`]
            : []),
          `${metrics.count} ${unit}`,
        ],
      };
    } else {
      const periodLabel =
        comparisonPeriod === ComparisonPeriod.Current
          ? this.i18n.t(`chart-actions.funnels.completed-current-period`)
          : this.i18n.t(`chart-actions.funnels.completed-past-period`);
      content = {
        text: `${metrics.overallConvPercent} ${periodLabel}`,
        subTextLines: [
          ...(stepIdx > 1
            ? [`${metrics.stepConvPercent} ${this.i18n.t(`chart-actions.funnels.completed-from-previous`)}`]
            : []),
          `${metrics.count} ${unit}`,
        ],
      };
    }
    return content;
  }

  private getDropOffSlottedContent({
    metrics,
    unit,
    stepIdx,
    comparisonPeriod = ComparisonPeriod.None,
  }: SlottedContentGeneratorArgs): SlottedContent[] {
    let slottedContent;
    if (comparisonPeriod === ComparisonPeriod.None) {
      slottedContent = [
        {
          text: `${metrics.overallDropOffPercent} ${this.i18n.t(`chart-actions.funnels.dropped-off-first`)}`,
          subTextLines: [
            ...(stepIdx > 1
              ? [`${metrics.stepDropOffPercent} ${this.i18n.t(`chart-actions.funnels.dropped-previous`)}`]
              : []),
          ],
        },
        {
          text: `${metrics.dropOffCount} ${unit} ${this.i18n.t(`chart-actions.funnels.dropped-off`)}`,
          subTextLines: [`${metrics.count} ${this.i18n.t(`chart-actions.funnels.completed-this-step`)}`],
        },
      ];
    } else {
      const dropOffPeriodLabel =
        comparisonPeriod === ComparisonPeriod.Current
          ? this.i18n.t(`chart-actions.funnels.droppoff-current-period`)
          : this.i18n.t(`chart-actions.funnels.droppoff-past-period`);
      slottedContent = [
        {
          text: `${metrics.overallDropOffPercent} ${dropOffPeriodLabel}`,
          subTextLines: [
            ...(stepIdx > 1
              ? [`${metrics.stepDropOffPercent} ${this.i18n.t(`chart-actions.funnels.droppoff-previous-step`)}`]
              : []),
            `${metrics.dropOffCount} ${unit} ${this.i18n.t(`chart-actions.funnels.dropped-off`)}`,
          ],
        },
      ];
    }
    return slottedContent;
  }

  private getConversionMetrics(segments: FunnelSegment[], segmentIdx: number, stepIdx: number): StepConversionMetrics {
    if (!segments[segmentIdx]) {
      return {
        count: commaizeNumber(0),
        stepConvPercent: formatPercent(0) as string,
        overallConvPercent: formatPercent(0) as string,
        dropOffCount: commaizeNumber(0),
        stepDropOffPercent: formatPercent(0) as string,
        overallDropOffPercent: formatPercent(0) as string,
      };
    }

    const step = segments[segmentIdx].steps[stepIdx];
    const {count, overall_conv_ratio: overallConvRatio, step_conv_ratio: stepConvRatio} = step;
    const previousStep = segments[segmentIdx].steps[stepIdx - 1];
    return {
      count: this.formatter(count, false),
      stepConvPercent: this.formatter(stepConvRatio, true),
      overallConvPercent: this.formatter(overallConvRatio, true),
      dropOffCount: this.formatter(previousStep ? previousStep.count - count : 0, false),
      stepDropOffPercent: this.formatter(1 - stepConvRatio, true),
      overallDropOffPercent: this.formatter(1 - overallConvRatio, true),
    };
  }
}
